import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, CircularProgress, Typography, Button, Paper } from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  ErrorOutline as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Block as BlockIcon
} from "@material-ui/icons";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)", // 64px = altura do AppBar
    width: "100%",
    overflow: "hidden",
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 48,
    flexShrink: 0,
  },
  backButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flex: 1,
    fontWeight: 500,
  },
  iframeContainer: {
    flex: 1,
    display: "flex",
    position: "relative",
    overflow: "hidden",
    minHeight: 0,
  },
  iframe: {
    width: "100%",
    height: "100%",
    border: "none",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  iframeHidden: {
    visibility: "hidden",
  },
  loadingContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.background.default,
  },
  errorContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    textAlign: "center",
  },
  errorIcon: {
    fontSize: 64,
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
  },
  blockIcon: {
    fontSize: 64,
    color: theme.palette.warning.main,
    marginBottom: theme.spacing(2),
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  blockedContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    textAlign: "center",
  },
  blockedPaper: {
    padding: theme.spacing(4),
    maxWidth: 500,
    textAlign: "center",
  },
  blockedTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
  },
  blockedDescription: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(2),
    justifyContent: "center",
    flexWrap: "wrap",
  },
}));

const ExternalIframe = () => {
  const classes = useStyles();
  const { linkId } = useParams();
  const history = useHistory();
  const iframeRef = useRef(null);
  const timeoutRef = useRef(null);

  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [iframeLoading, setIframeLoading] = useState(true);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchLink = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/company-external-links/${linkId}`);
        if (!isMounted) return;
        setLink(data);
        setError(null);

        // Quem decide se a página pode ser embutida é o servidor DELA, pelos
        // cabeçalhos X-Frame-Options / CSP frame-ancestors. Google, Gmail,
        // Drive, Docs e ChatGPT proíbem — e o navegador não avisa a página
        // hospedeira: o frame simplesmente nasce vazio. Por isso perguntamos ao
        // backend (que lê só os cabeçalhos do destino) antes de montar. Se o
        // link já está marcado para abrir em aba nova, nem precisa perguntar.
        if (data.openMode === "new_tab") {
          setIframeBlocked(true);
          setIframeLoading(false);
        } else {
          try {
            const { data: verdict } = await api.get(
              `/company-external-links/${linkId}/embed-check`
            );
            if (isMounted && verdict && verdict.embeddable === false) {
              setIframeBlocked(true);
              setIframeLoading(false);
            }
          } catch (e) {
            // Checagem indisponível não impede a tentativa — o timeout abaixo
            // continua valendo como rede de segurança.
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(i18n.t("externalIframe.errors.linkNotFound"));
        }
      }
      if (isMounted) {
        setLoading(false);
      }
    };

    if (linkId) {
      fetchLink();
    }

    return () => {
      isMounted = false;
    };
  }, [linkId]);

  // Rede de segurança para o que a checagem não pegou (site que responde
  // diferente para o nosso IP, por exemplo). Não dá para detectar o bloqueio
  // pelo evento do iframe: com X-Frame-Options o `onLoad` dispara igual, porque
  // o navegador carrega a própria página de erro dentro do frame.
  useEffect(() => {
    if (link && !iframeBlocked && iframeLoading) {
      timeoutRef.current = setTimeout(() => {
        setIframeBlocked(true);
        setIframeLoading(false);
      }, 8000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [link, iframeBlocked, iframeLoading]);

  const handleBack = () => {
    history.goBack();
  };

  const handleIframeLoad = () => {
    // Limpar timeout se o iframe carregou
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Com X-Frame-Options o `onLoad` dispara igual: o navegador carrega a
    // própria página de erro dentro do frame. O que separa um caso do outro é
    // o acesso ao documento — num carregamento cross-origin de verdade ele
    // lança SecurityError; no frame bloqueado o Firefox deixa `about:blank`
    // acessível e vazio.
    try {
      const doc = iframeRef.current?.contentDocument;
      const vazio = doc && !doc.body?.childElementCount;
      if (vazio && doc.location?.href === "about:blank") {
        setIframeBlocked(true);
      }
    } catch (e) {
      // SecurityError: é cross-origin de verdade, ou seja, carregou.
    }

    setIframeLoading(false);
  };

  const handleIframeError = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIframeBlocked(true);
    setIframeLoading(false);
  };

  const handleOpenNewTab = () => {
    if (link?.url) {
      window.open(link.url, "_blank", "noopener,noreferrer");
    }
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress />
          <Typography style={{ marginTop: 16 }}>
            {i18n.t("externalIframe.loading")}
          </Typography>
        </div>
      </div>
    );
  }

  if (error || !link) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            className={classes.backButton}
          >
            {i18n.t("externalIframe.back")}
          </Button>
        </div>
        <div className={classes.errorContainer}>
          <ErrorIcon className={classes.errorIcon} />
          <Typography variant="h6" className={classes.errorText}>
            {error || i18n.t("externalIframe.errors.generic")}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleBack}
          >
            {i18n.t("externalIframe.back")}
          </Button>
        </div>
      </div>
    );
  }

  // Se o iframe está bloqueado por CSP
  if (iframeBlocked) {
    return (
      <div className={classes.root}>
        <div className={classes.header}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            className={classes.backButton}
            size="small"
          >
            {i18n.t("externalIframe.back")}
          </Button>
          <Typography variant="subtitle1" className={classes.title}>
            {link.name}
          </Typography>
        </div>
        <div className={classes.blockedContainer}>
          <Paper className={classes.blockedPaper} elevation={2}>
            <BlockIcon className={classes.blockIcon} />
            <Typography variant="h6" className={classes.blockedTitle}>
              {i18n.t("externalIframe.blocked.title")}
            </Typography>
            <Typography variant="body2" className={classes.blockedDescription}>
              {i18n.t("externalIframe.blocked.description")}
            </Typography>
            <div className={classes.buttonGroup}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenNewTab}
              >
                {i18n.t("externalIframe.openNewTab")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleBack}
              >
                {i18n.t("externalIframe.back")}
              </Button>
            </div>
          </Paper>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          className={classes.backButton}
          size="small"
        >
          {i18n.t("externalIframe.back")}
        </Button>
        <Typography variant="subtitle1" className={classes.title}>
          {link.name}
        </Typography>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={handleOpenNewTab}
        >
          {i18n.t("externalIframe.openNewTab")}
        </Button>
      </div>
      <div className={classes.iframeContainer}>
        {iframeLoading && (
          <div className={classes.loadingContainer}>
            <CircularProgress />
            <Typography style={{ marginTop: 16 }}>
              {i18n.t("externalIframe.loadingPage")}
            </Typography>
          </div>
        )}
        <iframe
          ref={iframeRef}
          src={link.url}
          title={link.name}
          className={`${classes.iframe} ${iframeLoading ? classes.iframeHidden : ''}`}
          onLoad={handleIframeLoad}
          onError={handleIframeError}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
        />
      </div>
    </div>
  );
};

export default ExternalIframe;
